if (!customElements.get('product-card-carousel')) {
  /**
   * Product card image carousel for touch devices.
   * The carousel itself is pure CSS (scroll-snap, see _product-card-image.liquid);
   * this element keeps the pagination bullets in sync with the scroll position
   * and makes the carousel loop by cloning the first/last slide at the edges.
   * On hover devices the cards keep their hover logic and this element does nothing.
   */
  class ProductCardCarousel extends HTMLElement {
    connectedCallback() {
      // Init when the device is (or becomes) touch — the media can flip after
      // load, e.g. DevTools device emulation or attaching/detaching a mouse.
      this.touchMedia = window.matchMedia('(hover: none) and (pointer: coarse)');
      if (this.touchMedia.matches) {
        this.init();
      }
      else {
        this.touchMedia.addEventListener('change', () => this.init(), { once: true });
      }
    }

    init() {
      if (this.initialized) return;

      this.scroller = this.querySelector('[data-product-card-image]');
      this.bullets = Array.from(this.querySelectorAll('[data-carousel-bullet]'));
      if (!this.scroller || this.bullets.length < 2) return;

      this.initialized = true;
      // Cards can sit inside a Swiper slider; this class makes Swiper ignore
      // touches that start on the image scroller so the native scroll-snap
      // carousel receives them. Only added here (touch devices) so desktop
      // mouse-dragging of a surrounding slider keeps working over the image.
      this.scroller.classList.add('swiper-no-swiping');
      this.slides = Array.from(this.scroller.querySelectorAll('img'));
      this.createLoopClones();
      this.observeSlides();
      this.watchLoopEdges();
    }

    disconnectedCallback() {
      this.bulletObserver?.disconnect();
    }

    createLoopClones() {
      // The clones are appended (never prepended) and positioned at the edges
      // with flex order, so the primary image stays the first img in the
      // figure — product-swatches.js relies on that to swap the card image.
      this.cloneOfLast = this.slides[this.slides.length - 1].cloneNode(true);
      this.cloneOfFirst = this.slides[0].cloneNode(true);

      [this.cloneOfLast, this.cloneOfFirst].forEach((clone) => {
        clone.setAttribute('aria-hidden', 'true');
        clone.setAttribute('data-carousel-clone', '');
      });
      this.cloneOfLast.style.order = '-1';
      this.cloneOfFirst.style.order = '1';

      this.scroller.append(this.cloneOfLast, this.cloneOfFirst);

      // Start on the real first slide (position 1, after the leading clone)
      this.jumpTo(1);
    }

    observeSlides() {
      // Bullets follow whichever slide is (mostly) in view
      this.bulletObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            this.setActiveBullet(this.slideIndexFor(entry.target));
          });
        },
        { root: this.scroller,
          threshold: 0.6 },
      );
      [...this.slides, this.cloneOfLast, this.cloneOfFirst].forEach(slide => this.bulletObserver.observe(slide));
    }

    watchLoopEdges() {
      // Jump to the real counterpart the moment the scroll arrives at an edge
      // clone — checked on every scroll event instead of waiting for the
      // scroll to settle (scrollend/debounce), which left a dead window where
      // a follow-up swipe had nothing left to scroll to. Never jump while a
      // finger is down, so a slow drag isn't yanked around.
      this.scroller.addEventListener('touchstart', () => {
        this.touching = true;
      }, { passive: true });
      this.scroller.addEventListener('touchend', () => {
        this.touching = false;
        this.handleLoop();
      }, { passive: true });

      this.scroller.addEventListener('scroll', () => {
        if (!this.touching) this.handleLoop();
      }, { passive: true });
    }

    handleLoop() {
      const width = this.scroller.clientWidth;
      const position = this.scroller.scrollLeft;
      const lastClonePosition = (this.slides.length + 1) * width;

      // Within a couple of px of a clone's snap point counts as arrived on it
      if (position >= lastClonePosition - 2) {
        this.jumpTo(1);
      }
      else if (position <= 2) {
        this.jumpTo(this.slides.length);
      }
    }

    slideIndexFor(slide) {
      if (slide === this.cloneOfFirst) return 0;
      if (slide === this.cloneOfLast) return this.slides.length - 1;
      return this.slides.indexOf(slide);
    }

    jumpTo(position) {
      this.scroller.scrollTo({ left: position * this.scroller.clientWidth,
        behavior: 'instant' });
    }

    setActiveBullet(index) {
      if (index < 0) return;
      this.bullets.forEach((bullet, i) =>
        bullet.classList.toggle('product-card__carousel-bullet--active', i === index),
      );
    }

    /**
     * Replaces the card images with another set of urls (used by
     * linked-product-swatches). The DOM slides are reused: surplus slides and
     * bullets are hidden, and the loop clones are rebuilt when the touch
     * carousel is active. Extra urls beyond the rendered slide count are ignored.
     * On hover devices the old images crossfade into the new ones instead of
     * switching hard (see createFadeClones).
     */
    setImages(urls) {
      const scroller = this.scroller || this.querySelector('[data-product-card-image]');
      if (!scroller || !urls?.length) return;

      // Loop clones are stale now; rebuilt below when the carousel is active.
      // Fade clones of a previous, still running swap are dropped as well.
      scroller.querySelectorAll('[data-carousel-clone], [data-swap-fade]').forEach(clone => clone.remove());

      const imgs = Array.from(scroller.querySelectorAll('img'));
      const fadeClones = this.createFadeClones(imgs);

      imgs.forEach((img, index) => {
        const url = urls[index];
        img.hidden = !url;
        if (url) {
          img.src = url;
          img.srcset = '';
        }
      });

      this.fadeOutClones(fadeClones);

      const allBullets = Array.from(this.querySelectorAll('[data-carousel-bullet]'));
      const visibleSlides = imgs.filter(img => !img.hidden);
      allBullets.forEach((bullet, index) => {
        bullet.hidden = index >= visibleSlides.length;
      });

      if (!this.initialized) return;

      this.bulletObserver?.disconnect();
      this.slides = visibleSlides;
      this.bullets = allBullets.filter(bullet => !bullet.hidden);

      if (this.slides.length > 1) {
        this.createLoopClones();
        this.observeSlides();
      }
      else {
        scroller.scrollTo({ left: 0,
          behavior: 'instant' });
      }
      this.setActiveBullet(0);
    }

    /**
     * Crossfade for the image swap on hover devices: the visible images are
     * cloned on top of the stack (same grid cell, later in DOM order), the
     * originals get their new src underneath and the clones fade out. The
     * clones keep their classes, so a hovered secondary image fades out from
     * its hovered state. Skipped on the touch carousel (the clones would become
     * slides) and when the visitor prefers reduced motion.
     */
    createFadeClones(imgs) {
      if (this.initialized || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return [];

      return imgs
        .filter(img => !img.hidden)
        .map((img) => {
          const clone = img.cloneNode(true);
          clone.setAttribute('aria-hidden', 'true');
          clone.setAttribute('data-swap-fade', '');
          clone.loading = 'eager';
          img.parentElement.append(clone);
          return clone;
        });
    }

    fadeOutClones(clones) {
      clones.forEach((clone) => {
        const animation = clone.animate(
          { opacity: [getComputedStyle(clone).opacity, 0]},
          { duration: 300,
            easing: 'ease-out',
            fill: 'forwards' },
        );
        animation.finished
          .catch(() => {}) // cancelled by a newer swap, the clone is already removed
          .finally(() => clone.remove());
      });
    }
  }
  customElements.define('product-card-carousel', ProductCardCarousel);
}
